import { LayoutPreferenceContext } from '@/context/layoutPreference'
import { queryClient } from '@/queryClient'
import { createApiRequest } from '@/services/api/createApiRequest'
import {
    useBulkRestoreDocument,
    useBulkSoftDeleteDocument,
    useDuplicateDocument,
    useMoveDocument,
    useUpdateDocument,
} from '@/services/folders/mutations'
import {
    keyDeletedDocuments,
    keyDocument,
    keyDocuments,
    keyFavoriteDocuments,
    keySharedDocuments,
    useInfiniteDeletedDocuments,
    useInfiniteDocuments,
    useInfiniteFavoriteDocuments,
    useInfiniteRecentDocuments,
    useInfiniteSharedDocuments,
    useQueryDocument,
} from '@/services/folders/queries'
import { DocumentInterface } from '@/services/folders/types'
import { resolveDocumentMongoId } from '@/utils/mongo-id'
import { useCallback, useContext, useMemo, useState } from 'react'
import { FinderItem, SortConfig, SortDirection, toFinderItems, ViewMode } from '../types'

export type FinderSection = 'all' | 'recent' | 'favorites' | 'shared' | 'trash'

type ExpandedFolderPagination = {
    page: number
    hasNextPage: boolean
}

interface UseFinderOptions {
    onFileSelect?: (item: FinderItem) => void
    onError?: (error: Error) => void
    onDuplicateSuccess?: (message?: string) => void
    showOnly?: 'all' | 'folders' | 'files'
    selectionMode?: 'single' | 'multiple'
    documentTag?: string
}

export function useFinder(options: UseFinderOptions = {}) {
    const { contentView, setContentView, sortCriteria, setSortCriteria, sortOrder, setSortOrder } =
        useContext(LayoutPreferenceContext)

    // Map context values to Finder types
    const viewMode: ViewMode = contentView as ViewMode
    const setViewMode = (mode: ViewMode) => setContentView(mode)

    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
    const [folderOpenContext, setFolderOpenContext] = useState<'owned' | 'shared'>('owned')
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
    const [sortConfig, setSortConfigState] = useState<SortConfig>({
        field: sortCriteria as SortConfig['field'],
        direction: sortOrder as SortDirection,
    })
    const [nonRecentSortConfig, setNonRecentSortConfig] = useState<SortConfig>({
        field: sortCriteria as SortConfig['field'],
        direction: sortOrder as SortDirection,
    })
    const [searchQuery, setSearchQuery] = useState('')
    const [activeSection, setActiveSection] = useState<FinderSection>('all')
    const [previewItemId, setPreviewItemId] = useState<string | null>(null)
    const [navigationHistory, setNavigationHistory] = useState<(string | null)[]>([null])
    const [historyIndex, setHistoryIndex] = useState(0)

    // Tree view state for progressive disclosure
    const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set())
    const [expandedFolderChildren, setExpandedFolderChildren] = useState<Map<string, FinderItem[]>>(new Map())
    const [loadingFolderIds, setLoadingFolderIds] = useState<Set<string>>(new Set())
    const [loadingMoreFolderIds, setLoadingMoreFolderIds] = useState<Set<string>>(new Set())
    const [expandedFolderPagination, setExpandedFolderPagination] = useState<Map<string, ExpandedFolderPagination>>(
        new Map()
    )

    // Queries - using infinite scroll for main sections
    // Search is context-aware: each section includes search param when searching
    const isFolderParam = options.showOnly === 'folders' ? 'true' : options.showOnly === 'files' ? 'false' : undefined

    const {
        data: rootDocumentsPages,
        isLoading: isLoadingRoot,
        isFetching: isFetchingRoot,
        hasNextPage: hasNextRootPage,
        fetchNextPage: fetchNextRootPage,
        isFetchingNextPage: isFetchingNextRootPage,
    } = useInfiniteDocuments({
        enabled: activeSection === 'all' && !currentFolderId,
        queryParams: {
            sort: sortConfig.field,
            sort_by: sortConfig.direction,
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
        },
        pageSize: 50,
    })

    // Folder metadata (for breadcrumbs, folder info)
    const {
        data: folderData,
        isLoading: isLoadingFolder,
        isFetching: isFetchingFolder,
    } = useQueryDocument({
        enabled: activeSection === 'all' && !!currentFolderId,
        queryParams: {
            id: currentFolderId as string,
        },
    })

    // Folder contents (paginated) - for owned documents
    const {
        data: folderContentsPages,
        isLoading: isLoadingFolderContents,
        hasNextPage: hasNextFolderContentsPage,
        fetchNextPage: fetchNextFolderContentsPage,
        isFetchingNextPage: isFetchingNextFolderContentsPage,
    } = useInfiniteDocuments({
        enabled: activeSection === 'all' && !!currentFolderId && folderOpenContext === 'owned',
        queryParams: {
            parent: currentFolderId as string,
            sort: sortConfig.field,
            sort_by: sortConfig.direction,
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
        },
        pageSize: 50,
    })

    // Shared folder contents (paginated) - for shared documents when navigating into a shared folder
    const {
        data: sharedFolderContentsPages,
        isLoading: isLoadingSharedFolderContents,
        hasNextPage: hasNextSharedFolderContentsPage,
        fetchNextPage: fetchNextSharedFolderContentsPage,
        isFetchingNextPage: isFetchingNextSharedFolderContentsPage,
    } = useInfiniteSharedDocuments({
        enabled:
            (activeSection === 'shared' && !!currentFolderId) ||
            (activeSection === 'all' && !!currentFolderId && folderOpenContext === 'shared'),
        queryParams: {
            parent: currentFolderId as string,
            sort: sortConfig.field,
            sort_by: sortConfig.direction,
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
        },
        pageSize: 50,
    })

    const {
        data: recentPages,
        isLoading: isLoadingRecent,
        hasNextPage: hasNextRecentPage,
        fetchNextPage: fetchNextRecentPage,
        isFetchingNextPage: isFetchingNextRecentPage,
    } = useInfiniteRecentDocuments({
        enabled: activeSection === 'recent',
        queryParams: {
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
        },
        pageSize: 50,
    })

    const {
        data: favoritesPages,
        isLoading: isLoadingFavorites,
        hasNextPage: hasNextFavoritesPage,
        fetchNextPage: fetchNextFavoritesPage,
        isFetchingNextPage: isFetchingNextFavoritesPage,
    } = useInfiniteFavoriteDocuments({
        enabled: activeSection === 'favorites',
        queryParams: {
            favorite: 'true',
            sort: sortConfig.field,
            sort_by: sortConfig.direction,
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
        },
        pageSize: 50,
    })

    const {
        data: sharedPages,
        isLoading: isLoadingShared,
        hasNextPage: hasNextSharedPage,
        fetchNextPage: fetchNextSharedPage,
        isFetchingNextPage: isFetchingNextSharedPage,
    } = useInfiniteSharedDocuments({
        enabled: activeSection === 'shared' && !currentFolderId,
        queryParams: {
            sort: sortConfig.field,
            sort_by: sortConfig.direction,
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
        },
        pageSize: 50,
    })

    const {
        data: trashPages,
        isLoading: isLoadingTrash,
        hasNextPage: hasNextTrashPage,
        fetchNextPage: fetchNextTrashPage,
        isFetchingNextPage: isFetchingNextTrashPage,
    } = useInfiniteDeletedDocuments({
        enabled: activeSection === 'trash',
        queryParams: {
            include_children: 'false',
            sort: sortConfig.field,
            ...(searchQuery && { search: searchQuery }),
            ...(options.documentTag && { tag: options.documentTag }),
            sort_by: sortConfig.direction,
            ...(isFolderParam !== undefined && { isFolder: isFolderParam }),
        },
        pageSize: 50,
    })

    // Mutations
    const refetchKeys = useMemo(
        () => [
            keyDocuments(`data_type=info`),
            keyFavoriteDocuments(''),
            keySharedDocuments(''),
            keyDocument(currentFolderId ? `id=${currentFolderId}` : ''),
            keyDeletedDocuments(''),
        ],
        [currentFolderId]
    )

    const { mutate: moveDocument, isLoading: isMoving } = useMoveDocument({
        refetch: refetchKeys,
        onError: error => options.onError?.(error as Error),
    })

    const { mutate: updateDocument, isLoading: isUpdating } = useUpdateDocument({
        refetch: refetchKeys,
        onError: error => options.onError?.(error as Error),
    })

    const { mutate: bulkSoftDelete, isLoading: isDeleting } = useBulkSoftDeleteDocument({
        refetch: refetchKeys,
        onError: error => options.onError?.(error as Error),
    })

    const { mutate: duplicateDocument, isLoading: isDuplicating } = useDuplicateDocument({
        refetch: refetchKeys,
        onSuccess: () => {
            options.onDuplicateSuccess?.('Item duplicated')
        },
        onError: error => options.onError?.(error as Error),
    })

    const { mutate: bulkRestore, isLoading: isRestoring } = useBulkRestoreDocument({
        refetch: refetchKeys,
        onError: error => options.onError?.(error as Error),
    })

    const [isPermanentlyDeleting, setIsPermanentlyDeleting] = useState(false)

    // Helper to flatten infinite query pages
    const flattenPages = (pages: { data: DocumentInterface[] }[] | undefined): DocumentInterface[] => {
        if (!pages) return []
        return pages.flatMap(page => page?.data || [])
    }

    // Computed items - search is now handled within each section's query
    const items = useMemo((): FinderItem[] => {
        let rawItems: DocumentInterface[] = []

        if (activeSection === 'recent') {
            rawItems = flattenPages(recentPages?.pages)
        } else if (activeSection === 'favorites') {
            rawItems = flattenPages(favoritesPages?.pages)
        } else if (activeSection === 'shared') {
            if (currentFolderId) {
                rawItems = flattenPages(sharedFolderContentsPages?.pages)
            } else {
                rawItems = flattenPages(sharedPages?.pages)
            }
        } else if (activeSection === 'trash') {
            rawItems = flattenPages(trashPages?.pages)
        } else if (currentFolderId) {
            rawItems =
                folderOpenContext === 'shared'
                    ? flattenPages(sharedFolderContentsPages?.pages)
                    : flattenPages(folderContentsPages?.pages)
        } else {
            rawItems = flattenPages(rootDocumentsPages?.pages)
        }

        let finderItems = toFinderItems(rawItems)

        // Filter by type if showOnly is specified
        if (options.showOnly === 'folders') {
            finderItems = finderItems.filter(item => item.type === 'folder')
        } else if (options.showOnly === 'files') {
            finderItems = finderItems.filter(item => item.type === 'file')
        }

        return finderItems
    }, [
        activeSection,
        recentPages,
        favoritesPages,
        sharedPages,
        sharedFolderContentsPages,
        trashPages,
        currentFolderId,
        folderContentsPages,
        rootDocumentsPages,
        options.showOnly,
        folderOpenContext,
    ])

    const breadcrumbs = useMemo(() => {
        const crumbs: { id: string | null; name: string }[] = [{ id: null, name: 'Documents' }]

        if (folderData?.computed_breadcrumbs) {
            folderData.computed_breadcrumbs.forEach(bc => {
                const id = resolveDocumentMongoId(bc.id)
                if (id) crumbs.push({ id, name: bc.name })
            })
        }

        const currentId = resolveDocumentMongoId(currentFolderId) ?? resolveDocumentMongoId(folderData?.id)
        const currentName = folderData?.name
        if (currentId && currentName && !crumbs.some(c => c.id === currentId)) {
            crumbs.push({ id: currentId, name: currentName })
        }

        return crumbs
    }, [folderData, currentFolderId])

    // Only consider loading for active queries (isLoading && isFetching pattern handles disabled queries)
    const isLoading =
        (isLoadingRoot && isFetchingRoot) ||
        (isLoadingFolder && isFetchingFolder) ||
        (isLoadingFolderContents && activeSection === 'all' && !!currentFolderId && folderOpenContext === 'owned') ||
        (isLoadingSharedFolderContents &&
            ((activeSection === 'shared' && !!currentFolderId) ||
                (activeSection === 'all' && !!currentFolderId && folderOpenContext === 'shared'))) ||
        (isLoadingRecent && activeSection === 'recent') ||
        (isLoadingFavorites && activeSection === 'favorites') ||
        (isLoadingShared && activeSection === 'shared' && !currentFolderId) ||
        (isLoadingTrash && activeSection === 'trash')

    const isMutating = isMoving || isUpdating || isDeleting || isDuplicating || isRestoring || isPermanentlyDeleting

    const previewItem = useMemo(() => {
        if (!previewItemId) return null
        return items.find(item => item.id === previewItemId) || null
    }, [previewItemId, items])

    // Navigation
    const canGoBack = historyIndex > 0
    const canGoForward = historyIndex < navigationHistory.length - 1

    const navigateToFolder = useCallback(
        (folderId: string | null, addToHistory = true) => {
            if (addToHistory) {
                const newHistory = navigationHistory.slice(0, historyIndex + 1)
                newHistory.push(folderId)
                setNavigationHistory(newHistory)
                setHistoryIndex(newHistory.length - 1)
            }
            setCurrentFolderId(folderId)
            setSelectedIds(new Set())
            setCheckedIds(new Set())
            setPreviewItemId(null)
        },
        [navigationHistory, historyIndex]
    )

    const navigateToFolderWithContext = useCallback(
        (folderId: string | null, context: 'owned' | 'shared', addToHistory = true) => {
            setFolderOpenContext(context)
            navigateToFolder(folderId, addToHistory)
        },
        [navigateToFolder]
    )

    /** Jump to a breadcrumb level and replace navigation history (not push). */
    const navigateViaBreadcrumb = useCallback(
        (folderId: string | null) => {
            const normalizedTarget =
                folderId == null ? null : resolveDocumentMongoId(folderId) ?? null

            const pathFromTrail = breadcrumbs
                .map(c => (c.id == null ? null : resolveDocumentMongoId(c.id)))
                .filter((id, index) => index === 0 || id != null) as (string | null)[]

            const targetIndex = pathFromTrail.findIndex(id =>
                normalizedTarget == null ? id == null : id === normalizedTarget
            )

            const path =
                targetIndex >= 0
                    ? pathFromTrail.slice(0, targetIndex + 1)
                    : normalizedTarget
                      ? [null, normalizedTarget]
                      : [null]

            setNavigationHistory(path)
            setHistoryIndex(path.length - 1)
            setCurrentFolderId(normalizedTarget)
            setSelectedIds(new Set())
            setCheckedIds(new Set())
            setPreviewItemId(null)
        },
        [breadcrumbs]
    )

    const goBack = useCallback(() => {
        if (canGoBack) {
            const newIndex = historyIndex - 1
            setHistoryIndex(newIndex)
            setCurrentFolderId(navigationHistory[newIndex])
            setSelectedIds(new Set())
            setCheckedIds(new Set())
            setPreviewItemId(null)
        }
    }, [canGoBack, historyIndex, navigationHistory])

    const goForward = useCallback(() => {
        if (canGoForward) {
            const newIndex = historyIndex + 1
            setHistoryIndex(newIndex)
            setCurrentFolderId(navigationHistory[newIndex])
            setSelectedIds(new Set())
            setCheckedIds(new Set())
            setPreviewItemId(null)
        }
    }, [canGoForward, historyIndex, navigationHistory])

    const updateSortConfig = useCallback(
        (config: SortConfig) => {
            setSortConfigState(config)
            setSortCriteria(config.field as 'createdAt' | 'name' | 'last_opened' | 'updatedAt')
            setSortOrder(config.direction)
        },
        [setSortCriteria, setSortOrder]
    )

    const changeSection = useCallback(
        (section: FinderSection) => {
            const wasRecent = activeSection === 'recent'

            setActiveSection(section)
            setSearchQuery('')
            setSelectedIds(new Set())
            setCheckedIds(new Set())
            setPreviewItemId(null)
            setFolderOpenContext(section === 'shared' ? 'shared' : 'owned')

            if (section === 'all' || section === 'shared') {
                navigateToFolder(null, false)
            }

            // Preserve sort across sections. Recent always defaults to "Last Opened".
            if (section === 'recent') {
                setSortConfigState({ field: 'last_opened', direction: 'desc' })
                return
            }

            if (wasRecent) {
                updateSortConfig(nonRecentSortConfig)
            }
        },
        [activeSection, navigateToFolder, nonRecentSortConfig, updateSortConfig]
    )

    // Tree view: toggle folder expansion
    const toggleFolderExpand = useCallback(
        async (folderId: string) => {
            const isCurrentlyExpanded = expandedFolderIds.has(folderId)

            if (isCurrentlyExpanded) {
                // Collapse
                setExpandedFolderIds(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(folderId)
                    return newSet
                })
            } else {
                // Expand - fetch children if not already loaded
                setExpandedFolderIds(prev => new Set(prev).add(folderId))

                if (!expandedFolderChildren.has(folderId)) {
                    setLoadingFolderIds(prev => new Set(prev).add(folderId))

                    try {
                        const params = new URLSearchParams()
                        params.set('parent', folderId)
                        params.set('page', '1')
                        params.set('limit', '50')
                        params.set('sort', sortConfig.field)
                        params.set('sort_by', sortConfig.direction)
                        if (isFolderParam !== undefined) params.set('isFolder', isFolderParam)

                        const url =
                            activeSection === 'shared' || folderOpenContext === 'shared'
                                ? '/shared-documents'
                                : '/documents'
                        const response = await createApiRequest<{ data: DocumentInterface[]; pagination: any }>({
                            url,
                            method: 'GET',
                            queryParams: params.toString(),
                        })

                        const rawChildren = response?.data || []
                        const children = toFinderItems(rawChildren)

                        const pagination = response?.pagination
                        const total = Number(pagination?.total ?? 0)
                        const limit = Number(pagination?.limit ?? 50)
                        const page = Number(pagination?.page ?? 1)
                        const hasNextPage = page * limit < total

                        setExpandedFolderChildren(prev => new Map(prev).set(folderId, children))
                        setExpandedFolderPagination(prev => new Map(prev).set(folderId, { page, hasNextPage }))
                    } catch (error) {
                        console.error('Failed to fetch folder children:', error)
                    } finally {
                        setLoadingFolderIds(prev => {
                            const newSet = new Set(prev)
                            newSet.delete(folderId)
                            return newSet
                        })
                    }
                }
            }
        },
        [
            activeSection,
            expandedFolderIds,
            expandedFolderChildren,
            isFolderParam,
            sortConfig.direction,
            sortConfig.field,
            folderOpenContext,
        ]
    )

    const loadMoreFolderChildren = useCallback(
        async (folderId: string) => {
            const existing = expandedFolderPagination.get(folderId)
            if (!existing?.hasNextPage) return
            if (loadingMoreFolderIds.has(folderId)) return

            setLoadingMoreFolderIds(prev => new Set(prev).add(folderId))
            try {
                const nextPage = existing.page + 1

                const params = new URLSearchParams()
                params.set('parent', folderId)
                params.set('page', String(nextPage))
                params.set('limit', '50')
                params.set('sort', sortConfig.field)
                params.set('sort_by', sortConfig.direction)
                if (isFolderParam !== undefined) params.set('isFolder', isFolderParam)

                const url =
                    activeSection === 'shared' || folderOpenContext === 'shared' ? '/shared-documents' : '/documents'
                const response = await createApiRequest<{ data: DocumentInterface[]; pagination: any }>({
                    url,
                    method: 'GET',
                    queryParams: params.toString(),
                })

                const rawChildren = response?.data || []
                const nextItems = toFinderItems(rawChildren)

                const pagination = response?.pagination
                const total = Number(pagination?.total ?? 0)
                const limit = Number(pagination?.limit ?? 50)
                const page = Number(pagination?.page ?? nextPage)
                const hasNextPage = page * limit < total

                setExpandedFolderChildren(prev => {
                    const current = prev.get(folderId) || []
                    return new Map(prev).set(folderId, [...current, ...nextItems])
                })
                setExpandedFolderPagination(prev => new Map(prev).set(folderId, { page, hasNextPage }))
            } catch (error) {
                console.error('Failed to fetch more folder children:', error)
            } finally {
                setLoadingMoreFolderIds(prev => {
                    const next = new Set(prev)
                    next.delete(folderId)
                    return next
                })
            }
        },
        [
            activeSection,
            expandedFolderPagination,
            isFolderParam,
            loadingMoreFolderIds,
            sortConfig.direction,
            sortConfig.field,
            folderOpenContext,
        ]
    )

    // Actions
    const moveItems = useCallback(
        (itemIds: string[], destinationId: string | null) => {
            itemIds.forEach(itemId => {
                moveDocument({
                    itemId,
                    destinationId: destinationId || '',
                })
            })
        },
        [moveDocument]
    )

    const deleteItems = useCallback(
        (itemIds: string[]) => {
            bulkSoftDelete({ ids: itemIds })
            setCheckedIds(new Set())
            setSelectedIds(new Set())
        },
        [bulkSoftDelete]
    )

    const restoreItems = useCallback(
        (itemIds: string[]) => {
            bulkRestore({ ids: itemIds })
            setCheckedIds(new Set())
            setSelectedIds(new Set())
        },
        [bulkRestore]
    )

    const permanentlyDeleteItems = useCallback(
        async (itemIds: string[]) => {
            // Optimistic update - clear selection immediately
            setCheckedIds(new Set())
            setSelectedIds(new Set())
            setPreviewItemId(null)
            setIsPermanentlyDeleting(true)

            try {
                await createApiRequest({
                    url: '/documents',
                    method: 'DELETE',
                    queryParams: `ids=${itemIds.join(',')}`,
                })

                // Refetch all relevant queries after successful delete
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: keyDeletedDocuments('') }),
                    queryClient.invalidateQueries({ queryKey: keyDocuments(`data_type=info`) }),
                ])
            } catch (error) {
                options.onError?.(error as Error)
            } finally {
                setIsPermanentlyDeleting(false)
            }
        },
        [options]
    )

    const toggleFavorite = useCallback(
        (item: FinderItem) => {
            updateDocument({
                id: item.id,
                data: { favorite: !item.favorite },
            })
        },
        [updateDocument]
    )

    const renameItem = useCallback(
        (item: FinderItem, newName: string) => {
            updateDocument({
                id: item.id,
                data: { name: newName },
            })
        },
        [updateDocument]
    )

    const duplicate = useCallback(
        (item: FinderItem) => {
            const id = resolveDocumentMongoId(item.id) ?? resolveDocumentMongoId(item._id)
            if (!id) {
                options.onError?.(new Error('This item cannot be duplicated (invalid id)'))
                return
            }
            duplicateDocument({ id })
        },
        [duplicateDocument, options]
    )

    const refetch = useCallback(() => {
        queryClient.invalidateQueries(keyDocuments(''))
        queryClient.invalidateQueries(keyDocument(''))
    }, [])

    const handleItemDoubleClick = useCallback(
        (item: FinderItem) => {
            if (item.type === 'folder') {
                if (searchQuery) setSearchQuery('')
                navigateToFolderWithContext(item.id, activeSection === 'shared' ? 'shared' : 'owned')
                // Keep current section context when navigating into folders
                // (e.g., stay in 'shared' section when opening a shared folder)
            } else {
                options.onFileSelect?.(item)
            }
        },
        [activeSection, navigateToFolderWithContext, options, searchQuery]
    )

    const handleSortChange = useCallback(
        (field: SortConfig['field'], direction?: SortDirection) => {
            setSortConfigState(prev => {
                const newConfig: SortConfig = {
                    field,
                    direction: direction ?? (prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'),
                }
                if (activeSection !== 'recent') {
                    setNonRecentSortConfig(newConfig)
                }
                // Persist to context
                setSortCriteria(newConfig.field as 'createdAt' | 'name' | 'last_opened' | 'updatedAt')
                setSortOrder(newConfig.direction)
                return newConfig
            })
        },
        [activeSection, setSortCriteria, setSortOrder]
    )

    return {
        // State
        items,
        currentFolderId,
        selectedIds,
        setSelectedIds,
        checkedIds,
        setCheckedIds,
        viewMode,
        setViewMode,
        sortConfig,
        setSortConfig: handleSortChange,
        searchQuery,
        setSearchQuery,
        activeSection,
        previewItemId,
        setPreviewItemId,
        previewItem,
        breadcrumbs,

        // Loading states
        isLoading,
        isMutating,

        // Navigation
        canGoBack,
        canGoForward,
        navigateToFolder,
        navigateToFolderWithContext,
        navigateViaBreadcrumb,
        goBack,
        goForward,
        changeSection,

        // Actions
        moveItems,
        deleteItems,
        restoreItems,
        permanentlyDeleteItems,
        toggleFavorite,
        renameItem,
        duplicate,
        refetch,

        // Tree view (progressive disclosure)
        expandedFolderIds,
        expandedFolderChildren,
        loadingFolderIds,
        toggleFolderExpand,
        expandedFolderHasNextPage: useMemo(() => {
            const map = new Map<string, boolean>()
            expandedFolderPagination.forEach((value, key) => {
                map.set(key, value.hasNextPage)
            })
            return map
        }, [expandedFolderPagination]),
        loadingMoreFolderIds,
        loadMoreFolderChildren,
        handleItemDoubleClick,

        // Current folder data
        currentFolder: folderData,

        // Selection mode
        selectionMode: options.selectionMode || 'multiple',

        // Infinite scroll
        hasNextPage:
            (activeSection === 'all' && !currentFolderId && hasNextRootPage) ||
            (activeSection === 'all' &&
                !!currentFolderId &&
                folderOpenContext === 'owned' &&
                hasNextFolderContentsPage) ||
            (activeSection === 'all' &&
                !!currentFolderId &&
                folderOpenContext === 'shared' &&
                hasNextSharedFolderContentsPage) ||
            (activeSection === 'recent' && hasNextRecentPage) ||
            (activeSection === 'shared' && !currentFolderId && hasNextSharedPage) ||
            (activeSection === 'shared' && !!currentFolderId && hasNextSharedFolderContentsPage) ||
            (activeSection === 'favorites' && hasNextFavoritesPage) ||
            (activeSection === 'trash' && hasNextTrashPage),
        isFetchingNextPage:
            isFetchingNextRootPage ||
            isFetchingNextFolderContentsPage ||
            isFetchingNextSharedFolderContentsPage ||
            isFetchingNextRecentPage ||
            isFetchingNextSharedPage ||
            isFetchingNextFavoritesPage ||
            isFetchingNextTrashPage,
        fetchNextPage: () => {
            if (activeSection === 'all' && !currentFolderId && hasNextRootPage) {
                fetchNextRootPage()
            } else if (
                activeSection === 'all' &&
                !!currentFolderId &&
                folderOpenContext === 'owned' &&
                hasNextFolderContentsPage
            ) {
                fetchNextFolderContentsPage()
            } else if (
                activeSection === 'all' &&
                !!currentFolderId &&
                folderOpenContext === 'shared' &&
                hasNextSharedFolderContentsPage
            ) {
                fetchNextSharedFolderContentsPage()
            } else if (activeSection === 'recent' && hasNextRecentPage) {
                fetchNextRecentPage()
            } else if (activeSection === 'shared' && !currentFolderId && hasNextSharedPage) {
                fetchNextSharedPage()
            } else if (activeSection === 'shared' && !!currentFolderId && hasNextSharedFolderContentsPage) {
                fetchNextSharedFolderContentsPage()
            } else if (activeSection === 'favorites' && hasNextFavoritesPage) {
                fetchNextFavoritesPage()
            } else if (activeSection === 'trash' && hasNextTrashPage) {
                fetchNextTrashPage()
            }
        },
    }
}
